/*
 *   Copyright (c) 2021 vlinder Labs Private Limited
 *   All rights reserved.
 *   This software is proprietary of Vlinder Labs Pvt. Ltd and can not be distributed / used for any purpose outside of Vlinder Labs Pvt. Ltd without explicit written consent from the copyright owner.
 */

import {
  IAgentPlugin,
  IPluginMethodMap,
} from "@vlinder-web/plugin-agent-service-react";
import config from "../config/userLists.config.json";
import axios from "axios";

import { GraphqlServerModule } from "@vlinder-web/graphql-server-module-react";
import { GET_CURRENCY, GET_PAYMENT, GET_PRESENTATIONS } from "../graphql";
import { VApplication } from "../../../application";
import moment from "moment-timezone";
import { pretty } from "@vlinder-web/utils-react";

const getGraphQLModule = () => {
  const app = VApplication.getInstance();
  return app.getSync("services.GraphqlServerModule") as GraphqlServerModule;
};

export interface IUserListsMethods extends IPluginMethodMap {
  getUserListsProps(args: any): Promise<any>;
}

export class UserListsService implements IAgentPlugin {
  readonly methods: IUserListsMethods;

  constructor() {
    this.methods = {
      getUserListsProps: this.getUserListsProps.bind(this),
    };
  }

  async getUserListsProps(args: any) {
    const graphqlModule = getGraphQLModule();
    const client = graphqlModule.getClient("graphql-gateway");
    console.log("args 41==>", pretty(args))
    try {
      if (client) {
        const presentationsFilter = await client.query({
          query: GET_PRESENTATIONS,
          variables: {
            filter: {
              status: {
                eq: "active",
              },
              year: {
                eq: "2024"
              }
            },
          },
          fetchPolicy: "network-only",
        });
        const defaultPresentation = presentationsFilter?.data?.getPresentations?.content[0]?.id;
        let filter = {};
        filter = args?.criteria?.email === "" || args?.criteria?.email === undefined ? {
          presentation: {
            eq: args?.criteria?.presentation ?? defaultPresentation,
          }
        } : {
          presentation: {
            eq: args?.criteria?.presentation ?? defaultPresentation,
          },
          CreatedByUser___email: {
            eq: args?.criteria?.email
          }
        };
        if (args?.criteria?.startDate && args?.criteria?.endDate) {
          filter = {
            ...filter,
            createdAt: {
              gte: args?.criteria?.startDate,
              lte: args?.criteria?.endDate,
            }
          }
        }
        else if (args?.criteria?.startDate && !args?.criteria?.endDate) {
          filter = {
            ...filter,
            createdAt: {
              gte: args?.criteria?.startDate,
            }
          }
        }
        else if (!args?.criteria?.startDate && args?.criteria?.endDate) {
          filter = {
            ...filter,
            createdAt: {
              lte: args?.criteria?.endDate,
            }
          }
        }
        const res = await client.query({
          query: GET_PAYMENT,
          variables: {
            filter: filter
          },
          fetchPolicy: "network-only",
        });
        const curr = await client.query({
          query: GET_CURRENCY,
          fetchPolicy: "network-only",
        });

        if (res?.data) {
          let _rowData = this.formatUserTable(
            res?.data?.getPaymentStats?.data,
            curr?.data?.getCurrencyExchangeRate?.conversion_rate ?? 1,
            args?.criteria?.email,
            args?.criteria?.endDate,
            args?.criteria?.startDate
          );
          return {
            ...config,
            ultUsers: _rowData,
            isEmpty: res?.data?.getPaymentStats?.data?.length <= 0,
            PresentationFilter: presentationsFilter?.data?.getPresentations?.content?.map(
              (_item: any) => {
                return { value: _item?.id, label: _item?.name };
              }
            ),
          };
        }
      }
    } catch (err: any) {
      console.log(`error in table data`, err);
      let _display_message =
        err?.graphQLErrors &&
        Array.isArray(err?.graphQLErrors) &&
        err?.graphQLErrors.find((item: any) => item.extensions);
      if (_display_message?.extensions?.display_message) {
        throw Error(_display_message?.extensions?.display_message);
      }

      throw err;
    }
  }

  private formatUserTable(data: any, conversion_rate: any, email: any, startDate: any, endDate: any) {
    let _rowData: any[] = [];
    let totalCount: any = 0, totalamountInTTD: any = 0, totalamountInUSD: any = 0
    if ((email === "" || email === undefined) &&
      (startDate === "" || startDate === undefined) &&
      (endDate === "" || endDate === undefined)) {
      data.forEach((item: any) => {
        if (item?.name !== "BookingTotal" && item?.name !== "Card") {
          totalCount += item?.count;
          totalamountInTTD += item?.totalAmount;
          totalamountInUSD += (item?.totalAmount / conversion_rate)
          _rowData.push({
            name: item?.name.replace("_", " "),
            count: item?.count ?? 0,
            amountInTTD: item?.totalAmount.toFixed(2) ?? 0,
            amountInUSD: ((item?.totalAmount / conversion_rate).toFixed(2))
          })
        }
      })
      data.forEach((item: any) => {
        if (item?.name === "BookingTotal") {
          let amtInTTD: any = item?.totalAmount.toFixed(2) ?? 0
          let formattedAmtTTD = parseFloat(amtInTTD).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
          let amtInUSD: any = ((item?.totalAmount / conversion_rate).toFixed(2)) ?? 0
          let formattedAmtUSD = parseFloat(amtInUSD).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
          _rowData.unshift({
            name: "Booking Total",
            count: item?.count ?? 0,
            amountInTTD: `$ ${formattedAmtTTD}` ?? 0,
            amountInUSD: `$ ${formattedAmtUSD}` ?? 0
          })
        }
      })
      let amtInTTD: any = totalamountInTTD.toFixed(2) ?? 0
      let formattedAmtTTD = parseFloat(amtInTTD).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      let amtInUSD: any = totalamountInUSD.toFixed(2) ?? 0
      let formattedAmtUSD = parseFloat(amtInUSD).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

      _rowData.push({
        name: "GRAND TOTAL",
        count: totalCount,
        amountInTTD: `$ ${formattedAmtTTD}` ?? 0,
        amountInUSD: `$ ${formattedAmtUSD}` ?? 0
      })
    }
    else {
      data.forEach((item: any) => {
        if (item?.name !== "BookingTotal" && item?.name !== "Card") {
          totalCount += item?.count;
          totalamountInTTD += item?.totalAmount;
          totalamountInUSD += (item?.totalAmount / conversion_rate)
          _rowData.push({
            name: item?.name.replace("_", " "),
            count: item?.count ?? 0,
            amountInTTD: item?.totalAmount.toFixed(2) ?? 0,
            amountInUSD: ((item?.totalAmount / conversion_rate).toFixed(2))
          })
        }
      })
      // if (totalCount !== 0) {
      let amtInTTD: any = totalamountInTTD.toFixed(2) ?? 0
      let formattedAmtTTD = parseFloat(amtInTTD).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      let amtInUSD: any = totalamountInUSD.toFixed(2) ?? 0
      let formattedAmtUSD = parseFloat(amtInUSD).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      _rowData.push({
        name: "GRAND TOTAL",
        count: totalCount,
        amountInTTD: `$ ${formattedAmtTTD}` ?? 0,
        amountInUSD: `$ ${formattedAmtUSD}` ?? 0
      })
      // }
      // else {
      //   _rowData = [];
      // }
    }
    return _rowData;
  }
}
